import { type ReactNode } from "react";
import { useRuntimeConfig } from "../config-context.js";
import { useLocale } from "../i18n/provider.js";
import { useRealtime } from "../realtime/context.js";

/**
 * **وجهُ الشخص — واحدٌ في النظام كلِّه.**
 *
 * والبلوبرنت (§7) يجعل بطاقةَ الشخص قاعدةً مُلزِمة: «كانت موجودة في v2
 * **كنسختين متفرّقتين**، فعملت كل نسخة في مكان — ومن هنا شعور (موجودة في بعض
 * الشاشات فقط)». وهذا المكوّنُ هو **نواتُها**: الوجهُ وحده الآن، ويُبنى عليه
 * باقيها (الاسم والدور والقسم وزرُّ المحادثة) مع موديول الشات.
 *
 * **ومن لا صورةَ له يُعرَض بأوّل حرفين من اسمه، لا بمربّعٍ فارغ.**
 *
 * **وحالةُ الاتصال نزلت معه** — نقطةٌ واحدةٌ على الوجه، **ومصدرُها جلسةُ
 * المقبس** لا عمودٌ في جدول: فعمودٌ يُكتَب عند الدخول ولا يُمحى عند انقطاع
 * الشبكة **يُظهر نصفَ الفريق «متاحاً» وهم نائمون**.
 *
 * **وممنوعةٌ في البورتال نهائياً** (§7): «ظهورُها تسريبُ هوية». **والمنعُ
 * بنيويّ لا بشرطٍ هنا**: القوقعةُ البورتالية لا تُركّب القناةَ اللحظية، فخريطةُ
 * الحضور عندها فارغةٌ أبداً — فلا نقطةَ تُرسَم ولو ظهر الوجهُ في شاشتها.
 */
export function PersonPhoto({
  userId,
  name,
  stamp,
  size = "sm",
}: {
  readonly userId: string;
  readonly name: string;
  /**
   * **ختمُ آخر تغيير — وبلاه يبقى الوجهُ القديم بعد تبديله.**
   *
   * فالمسارُ واحدٌ لكل شخص، **والمتصفّحُ يخزّنه ساعة**. والختمُ في الاستعلام
   * يجعل الرابطَ مختلفاً عند كل تبديل. و`null` تعني «لا صورة».
   */
  readonly stamp: string | null | undefined;
  /**
   * **ثلاثةُ مقاسات، ومفرداتٌ موجودة.**
   *
   * `xs` هي `mini` (٢٢px) على الكروت والصفوف، و`sm` هي `av` (٣٠px) في القوقعة،
   * و`lg` في شاشة الملفّ. **ولا صنفَ جديد**: الثلاثةُ معرَّفةٌ في الورقة أصلاً،
   * وكانت تُكتَب في خمسة مواضعَ بأحرفٍ أولى من **أربع نسخٍ مختلفة** للدالّة
   * نفسِها — نسختان منها بلا حارسِ «لا اسم»، فتعرضان دائرةً فارغة.
   */
  readonly size?: "xs" | "sm" | "lg";
}): ReactNode {
  /**
   * **وعنوانُ السيرفر من السياق، لا بروباً في كل موضع.**
   *
   * فهو يُقرأ وقت التشغيل من `config.json` — وهذا ما يجعل نفسَ الحزمة تعمل
   * عند المشتري وفي الديمو وعند TWING (وتسعُ حوادثَ في v2 سببُها عنوانٌ
   * مخبوزٌ وقت البناء). **وتمريرُه بروباً كان سيصير واجباً على كل شاشةٍ
   * ترسم وجهاً** — واجبٌ يتكرّر يُنسى في أحد مواضعه حتماً.
   */
  const { apiBaseUrl } = useRuntimeConfig();
  const shape = size === "xs" ? "mini" : size === "lg" ? "av lg" : "av";
  const pixels = size === "xs" ? 22 : size === "lg" ? 72 : 30;
  /**
   * **والحالةُ مدفوعةٌ من السيرفر** — ولا تُسأل عن أحد.
   *
   * **ولا يُسأل عن حضورِ هذا الشخص بعينه**: فسؤالٌ بمعرّفٍ هو ثقبُ v2 المسجَّل
   * («يقبل قائمةَ أشخاصٍ ويسأل من فيهم متّصل»). فالخريطةُ تصل كاملةً **لمن
   * يجوز له**، والمكوّنُ يقرأ منها — ومن ليس فيها لا نقطةَ له.
   */
  const state = useRealtime().presence.get(userId);
  const { t } = useLocale();

  /**
   * **وما ليس ختماً نصّياً فلا صورة.**
   *
   * وكتبتُها `stamp === null` وحدها، **فمسارٌ لم يُعِد الحقلَ أصلاً أعطى
   * `undefined`** — وهي ليست `null`، فرُسمت `<img src=".../photo?v=undefined">`
   * فظهرت **أيقونةُ صورةٍ مكسورة في كل صفٍّ من دليل الموظفين**. ورأيتُها
   * بالعين، ولم يقلها اختبارٌ ولا مترجِم: الحقلُ مُعلَنٌ في النوع، والمسارُ
   * وحده نسيه.
   *
   * ومكوّنُ الوجه **واحدٌ في النظام كلِّه**، فحارسُه هنا يحمي كلَّ شاشة.
   */
  /**
   * **والنقطةُ أخٌ للوجه لا ابنٌ له** — فالوجهُ قد يكون `<img>`، ولا ابنَ له.
   *
   * ويُغلَّف الاثنان في مجلّدٍ واحدٍ له موضعٌ نسبيّ، **فلا تُكتَب النقطةُ
   * مرّتين** — مرّةً للصورة ومرّةً للحرفين.
   */
  const dot =
    state === undefined ? null : (
      /**
       * **وللنقطةِ نصٌّ يُقرأ** — فلونٌ وحده لا يصل من يقرأ بالصوت.
       *
       * **ولا لونَ يحمل معنىً وحده** (قاعدةُ الوصول في §7): فالنقطةُ لها
       * `role="img"` واسمٌ من القاموس — «متّصل» أو «بعيد» — فمن يكبّر الخطَّ
       * أو يقرأ بالصوت يعرف الحالةَ كما يعرفها من يرى الأخضر.
       */
      <i
        className={`pres ${state === "away" ? "away" : "on"}`}
        role="img"
        aria-label={t(state === "away" ? "chat.presence.away" : "chat.presence.online")}
        title={t(state === "away" ? "chat.presence.away" : "chat.presence.online")}
      />
    );

  if (typeof stamp !== "string" || stamp === "") {
    return (
      <span className="face">
        <span className={shape} title={name} aria-hidden="true">
          {initialsOf(name)}
        </span>
        {dot}
      </span>
    );
  }

  return (
    <span className="face">
      <img
        className={shape}
        src={`${apiBaseUrl}/people/${userId}/photo?v=${encodeURIComponent(stamp)}`}
        alt={name}
        title={name}
        /** **ولا يُمدّ الوجه** — فصورةٌ ممطوطةٌ أسوأ من حرفين */
        width={pixels}
        height={pixels}
      />
      {dot}
    </span>
  );
}

/**
 * **أوّلُ حرفين** — ومن اسمٍ عربيٍّ أو إنجليزيّ سواء.
 *
 * وكانت مكتوبةً في القوقعة وحدها، **فلم يكن لغير صاحب الجلسة وجهٌ أصلاً**.
 *
 * **وحين لا اسم، لا يُعرَض المعرّفُ الخام** — وهي ملاحظةُ النسخة التي حلّت
 * محلَّها، ورآها كاتبُها في المتصفّح فعلاً: **دائرةٌ فيها UUID مكان اسم إنسان**.
 */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  return parts
    .slice(0, 2)
    .map((part) => [...part][0] ?? "")
    .join("");
}
